import { memo, useCallback } from 'react';
import * as emailjs from 'emailjs-com';
import isEmail from 'validator/es/lib/isEmail';
import { Form, Field, FormError } from '../utils/form';
import StepperButtons from './StepperButtons';
import { FORM_ERROR } from 'final-form';
import postApi from './postApi';

/* -------------------------------------------------------------------------- */
/* Tailwind input estilo “boxed”                                              */
/* -------------------------------------------------------------------------- */
const TextInput = ({
  label,               // lo usaremos como placeholder
  helperText,
  error,
  type = 'text',
  value,
  onChange,
  ...rest
}) => (
  <div className="w-full">
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={`${label}${rest.required ? ' *' : ''}`}
      className={`w-full rounded border px-3 py-2 placeholder-gray-500
                 focus:outline-none focus:ring-2 focus:ring-[#00897b]
                 ${error ? 'border-red-500 focus:ring-red-500' : 'border-gray-400'}`}
      {...rest}
    />
    {helperText && (
      <span className={`mt-1 text-xs ${error ? 'text-red-500' : 'text-gray-500'} block`}>
        {helperText}
      </span>
    )}
  </div>
);

/* -------------------------------------------------------------------------- */
/* EmailJS helper                                                             */
/* -------------------------------------------------------------------------- */
const sendRecord = async (info) => {
  if (import.meta.env.MODE !== 'production') return;
  await emailjs.send('gmail', 'template_EsXUTrgk', info, 'user_fN71bDhWcK6IbszbAzJEH');
};

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */
const RegisterContact = ({ data, nextStep, stepperProps }) => {
  const validateEmail = useCallback(
    (v) => (v && !isEmail(v) ? 'A valid email is required' : undefined),
    []
  );

  const submit = useCallback(
    async (clientInfo) => {
      try {
        const { firstName, lastName, email, phoneNumber } = clientInfo;
        const { paymentIntentClientSecret } = await postApi('/createRetreatCustomerPayment', {
          firstName, lastName, email, phoneNumber, package: data.package,
        });

        await sendRecord(clientInfo);
        nextStep({ ...clientInfo, paymentIntentClientSecret });
      } catch (err) {
        console.error(err);
        return { [FORM_ERROR]: 'An unexpected error occurred' };
      }
    },
    [data.package, nextStep]
  );

  return (
    <Form initialValues={data} onSubmit={submit}>
      {() => (
        <>
          <div className="p-6 mb-8">
            <h1 className="text-[#00897b] font-caveat text-5xl mb-6 text-left">Get started now:</h1>

            {/* grid de 4 columnas iguales */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Field
                component={TextInput}
                name="firstName"
                label="First Name"
                autoComplete="given-name"
                required
              />
              <Field
                component={TextInput}
                name="lastName"
                label="Last Name"
                autoComplete="family-name"
                required
              />
              <Field
                component={TextInput}
                type="email"
                name="email"
                label="Email Address"
                autoComplete="email"
                validate={validateEmail}
                required
              />
              <Field
                component={TextInput}
                type="tel"
                name="phoneNumber"
                label="Phone Number"
                autoComplete="tel"
                required
              />
            </div>

            <FormError className="mt-4" />
          </div>

          <StepperButtons {...stepperProps} />
        </>
      )}
    </Form>
  );
};

export default memo(RegisterContact);
